import { IconButton } from '@mui/material';
import { Stack } from '@mui/system';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { MinusIcon } from '@/assets/icons';
import TargetIcon from '@/assets/icons/TargetIcon';
import { SelectInput } from '@/modules/common/components/inputs/components/SelectInput';
import { usePropertyPanelConfirm } from '@/modules/common/components/usePropertyPanelConfirm';
import { useAreaPicker } from '@/modules/canvas/hooks/useAreaPicker';

interface TargetInputProps {
  value?: string;
  onTargetSelect: (value: string, jitRoutingPointId?: string, oldValue?: string) => void;
  onRemove?: () => void;
  dropdownItems: { label: string; value: string }[];
  disableRemove?: boolean;
  activateOnStart?: boolean;
  disabled?: boolean;
  areaType?: string;
  onPickerCancel?: () => void;
}

const TargetInputCanvasComponent = ({
  value,
  onTargetSelect,
  onRemove,
  dropdownItems,
  disableRemove,
  activateOnStart = false,
  disabled = false,
  areaType,
  onPickerCancel,
}: TargetInputProps) => {
  const { t } = useTranslation('interface');
  const { Dialog } = usePropertyPanelConfirm();
  const ref = useRef<HTMLDivElement>(null);
  const picker = useAreaPicker();

  const valueRef = useRef(value);
  const onTargetSelectRef = useRef(onTargetSelect);
  const onPickerCancelRef = useRef(onPickerCancel);
  const isPickingRef = useRef(false);
  const didActivateOnStartRef = useRef(false);
  // plz fix me
  // eslint-disable-next-line react-hooks/refs
  valueRef.current = value;
  // plz fix me
  // eslint-disable-next-line react-hooks/refs
  onTargetSelectRef.current = onTargetSelect;
  // plz fix me
  // eslint-disable-next-line react-hooks/refs
  onPickerCancelRef.current = onPickerCancel;

  const hasOptions = dropdownItems.length > 0;

  const enabledAreaIds = useMemo(() => dropdownItems.map((item) => item.value), [dropdownItems]);

  const handleActivateChooseShape = useCallback(() => {
    if (isPickingRef.current || disabled || !hasOptions) return;

    const message = areaType
      ? t('canvas_overlay.pick_area', {
          areaType,
          defaultValue: 'Please select a {{areaType}} on the canvas',
        })
      : t('canvas_overlay.pick_area_generic', 'Please select an area on the canvas');

    isPickingRef.current = true;

    picker.start({
      enabledAreaIds,
      onPick: (areaId) => {
        onTargetSelectRef.current(areaId, undefined, valueRef.current);
      },
      onCancel: () => {
        isPickingRef.current = false;
        onPickerCancelRef.current?.();
      },
      message,
    });
  }, [areaType, disabled, enabledAreaIds, hasOptions, picker, t]);

  useEffect(() => {
    if (!isPickingRef.current) return;

    picker.refresh(enabledAreaIds);
  }, [enabledAreaIds, picker]);

  const handleChange = useCallback(
    (selectedShapeId: string) => {
      onTargetSelect(selectedShapeId, undefined, value);
    },
    [onTargetSelect, value],
  );

  useEffect(() => {
    if (!activateOnStart || didActivateOnStartRef.current || !hasOptions) return;

    didActivateOnStartRef.current = true;
    handleActivateChooseShape();
  }, [activateOnStart, handleActivateChooseShape, hasOptions]);

  return (
    <Stack direction='row' alignItems='center'>
      <Dialog anchor={ref} offset={[0, 100]} />
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        ref={ref}
        spacing={1}
        sx={{
          flex: 1,
          minWidth: 0,
          width: '100%',
        }}
      >
        <SelectInput
          ellipsis
          disabled={disabled || !hasOptions}
          options={dropdownItems}
          onChange={handleChange}
          value={value}
          placeholder={
            hasOptions
              ? undefined
              : t('properties.flow_stops.no_areas_available', 'No areas available')
          }
        />

        <IconButton
          disabled={disabled || !hasOptions}
          onClick={handleActivateChooseShape}
          size='small'
          sx={{ p: 0, height: 'fit-content' }}
        >
          <TargetIcon />
        </IconButton>

        <IconButton
          disabled={disabled || disableRemove}
          onClick={onRemove}
          size='small'
          sx={{ p: 0, height: 'fit-content' }}
        >
          <MinusIcon />
        </IconButton>
      </Stack>
    </Stack>
  );
};

export const TargetInputCanvas = memo(TargetInputCanvasComponent);
